/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import moment from 'moment';
import { ItemSelection } from 'src/types';

import { endOfMonth, endOfQuarter } from '../utils/dates/date-utility';
import { LanguageCode, LanguageDefinition } from './types';
import { isNonProductionUrl } from 'src/utils/env.utils';

export const NBSP = '\u00A0';
export const NBHPH = '\u2011'; // A non-breaking hyphen e.g.: 9-5 -> `9${NBHPH}5`

export const isYearAfter2024 = () => getCurrentYear() > 2024;

export const getCurrentYear = () => {
  let currentYear = new Date().getFullYear();
  if (isNonProductionUrl()) {
    const parsedYear = Number(
      new URLSearchParams(window.location.search).get('year')
    );
    currentYear =
      parsedYear > 0 && !isNaN(parsedYear) ? parsedYear : currentYear;
  }
  return currentYear;
};

/**
 * @param regex - regular expression for host to be matched against
 * window.location.origin.match(REGEX_PARAMETER) === null is used at the core of the function
 * so... if regex = /sit|dev|stage|localhost/g when used against https://wwwdev.troweprice.com/
 * WARNING THIS MAY DO THE OPPOSITE OF WHAT YOU EXPECT
 * it will return false because it finds the existence of 'dev' in the host
 * so you will need to negate to checkExistence
 */
const checkHost = (regex: RegExp) => {
  let prod = true;
  if (window?.location?.origin) {
    prod = window.location.origin.match(regex) === null;
  } else {
    prod =
      typeof window.location.host !== 'undefined'
        ? window.location.host.match(regex) === null
        : prod;
  }
  return prod;
};

const settings = {
  clientName: 'T. Rowe Price',
  clientUrl: 'http://www.troweprice.com',
  currentYear: moment().format('YYYY'),
  iraContributionWindowCloseDate: moment('2019-04-17'),
  siteTitle: 'Self Serve Digital Journey',
  addresses: {
    bank: {
      mailingAddress: {
        recipient: 'T. Rowe Price',
        addressLine1: 'P.O. Box 17300',
        city: 'Baltimore',
        state: 'MD',
        zipCode: '21297-1300',
      },
    },
  },
  dates: {
    endOfMonth: endOfMonth(),
    endOfQuarter: endOfQuarter(),
  },
  personalInvestingURL:
    'https://www.troweprice.com/personal-investing/home.html',
  isProd: checkHost(/sit|dev|stage|localhost/g),
  isLocal: !checkHost(/localhost/g),
  isStage: !checkHost(/stage/g),
};

export default settings;

export const APP_LOADED = 'APP_LOADED';
export const AUTOMATIC_UPDATE_RELOAD = 'AUTOMATIC_UPDATE_RELOAD';

export const RETIREMENT = 'RETIREMENT';
export const NON_RETIREMENT = 'NON_RETIREMENT';
export const COLLEGE_SAVINGS = 'COLLEGE_SAVINGS';

export const CURRENT_YEAR = 'current';
export const PRIOR_YEAR = 'prior';

export const BUY_VANITY_HEADERS = {
  Contribute: 'Contribute to Your IRA',
  contribute: 'Contribute to Your IRA',
  contribute2: 'Contribute to Your IRA',
};

export const EVERIFY_TEST_MODES = {
  PASSED: 'passed',
  RANDOM: 'random',
  INTERNAL_APP_ERROR: 'internal_app_error',
  INDIVIDUAL_NOT_FOUND: 'individual_not_found',
  FAILED_IAUTH_QUESTIONS: 'failed_iauth_questions',
  DENIAL_IAUTH_FAILED_TOO_RECENTLY: 'denial_iauth_failed_too_recently',
  TRANSACTION_EXPIRED: 'TRANSACTION_EXPIRED',
};

export const FAILED = 'FAILED';
export const BLOCKED = 'BLOCKED';
export const TIME_OUT = 'TIME_OUT';
export const LOCK_OUT = 'LOCK_OUT';
export const BAIL_OUT = 'BAIL_OUT';
export const SYSTEM_FAILURE = 'SYSTEM_FAILURE';

export const PROGRESS_FLAG_SELL_WITHHOLDING_WARNED =
  'PROGRESS_FLAG_SELL_WITHHOLD_WARNED';

export const EVERIFY_FAILURES = {
  [FAILED]: FAILED,
  [BLOCKED]: BLOCKED,
  [TIME_OUT]: TIME_OUT,
  [LOCK_OUT]: LOCK_OUT,
  [BAIL_OUT]: BAIL_OUT,
  [SYSTEM_FAILURE]: SYSTEM_FAILURE,
};

export enum VERIFICATION_STATUS {
  INITIATED = 'INITIATED',
  READY_FOR_VALIDATION = 'READY_FOR_VALIDATION',
  VERIFIED = 'VERIFIED',
  REINITIATED = 'REINITIATED',
  INITIATION_FAIL = 'INITIATION_FAIL',
  FAILED = 'FAILED',
  LAPSED = 'LAPSED',
  DELETED = 'DELETED',
  DEPOSIT_FAIL = 'DEPOSIT_FAIL',
}

export enum BANK_STATUS {
  VERIFY_MICRO_DEPOSITS = 'verifyMicroDeposits',
  PREPARING_MICRO_DEPOSITS = 'preparingMicroDeposits',
  PROCESSING = 'processing',
  JUST_ADDED = 'justAdded',
  REQUIRES_RENEWAL = 'requiresRenewal',
  DELETED = 'deleted',
  CORRUPT_BANK_DATA = 'corruptBankData',
  LAPSED = 'lapsed',
  NO_ACH_REDEMPTION = 'noACHRedemption',
  NO_WIRE_REDEMPTION = 'noWireRedemption',
  NO_MAIL_REDEMPTION = 'noWireRedemption',
  NOT_ASSOCIATED = 'noteAssociated',
  CHASE_BANK = 'CHASE_BANK',
  REQUIRES_UPDATE_955 = 'REQUIRES_UPDATE_955',
}

export const eligibleBankStatusesMap = {
  [VERIFICATION_STATUS.INITIATED]: true,
  [VERIFICATION_STATUS.READY_FOR_VALIDATION]: true,
  [VERIFICATION_STATUS.VERIFIED]: true,
  [VERIFICATION_STATUS.REINITIATED]: true,
  [VERIFICATION_STATUS.LAPSED]: true,
  [VERIFICATION_STATUS.FAILED]: true,
};

export enum CONSTANTS {
  ROLLOVER = 'ROLLOVER_IRA',
  TRANSFER = 'TRANSFER_IRA',
  CONTRIBUTORY = 'CONTRIBUTORY_IRA',
  TRADITIONAL = 'TRADITIONAL_IRA',
  ROTH = 'ROTH_IRA',
  SEP = 'SEP_IRA',
}

export const NON_FIDUCIARY_PLAN = 'NON_FIDUCIARY_RETIREMENT_PLAN';

export const GROSS_AMOUNT = 'grossAmount';
export const GROSS_UNIT = 'grossUnit';

export enum ProductTypes {
  RAS = 'RETIREMENT_ADVISORY',
  ACTIVEPLUS = 'ACTIVE_PLUS_PORTFOLIO',
  ALASKA529 = 'Alaska529',
  BROKERAGE = 'BROKERAGE',
  MARYLAND529 = 'Maryland529',
  MUTUALFUND = 'MUTUAL_FUND',
  NATIONAL529 = 'National529',
  TAXABLE = 'TAXABLE',
}

export enum AGREEMENT_TYPES {
  COLLEGE_SAVINGS = 'COLLEGE_SAVINGS',
  CORPORATE = 'CORPORATE',
  DEFINED_BENEFIT = 'DEFINED_BENEFIT',
  EDUCATION_SAVINGS_ACCOUNT = 'EDUCATION_SAVINGS_ACCOUNT',
  ESTATE = 'ESTATE',
  FIS = 'FIS',
  GUARANTEED_INVESTMENT_CONTRACT = 'GUARANTEED_INVESTMENT_CONTRACT',
  INDIVIDUAL_401K = 'INDIVIDUAL_401K',
  INDIVIDUAL = 'INDIVIDUAL',
  JOHN_HANCOCK_GUARDIAN = 'JOHN_HANCOCK_GUARDIAN',
  JOHN_HANCOCK_TRUST = 'JOHN_HANCOCK_TRUST',
  JOHN_HANCOCK_UGMA_UTMA = 'JOHN_HANCOCK_UGMA_UTMA',
  JOINT_TENANT = 'JOINT_TENANT',
  KEOGH = 'KEOGH',
  MONEY_PURCHASE_PENSION_PLAN = 'MONEY_PURCHASE_PENSION_PLAN',
  NON_FIDUCIARY_RETIREMENT_PLAN = 'NON_FIDUCIARY_RETIREMENT_PLAN',
  NON_PROFIT_ORGANIZATION = 'NON_PROFIT_ORGANIZATION',
  NON_QUALIFIED_RPS = 'NON_QUALIFIED_RPS',
  ORGANIZATION = 'ORGANIZATION',
  OTHER = 'OTHER',
  POWER_OF_ATTORNEY = 'POWER_OF_ATTORNEY',
  PROFIT_SHARING_PLAN = 'PROFIT_SHARING_PLAN',
  RETIREMENT_PLAN_401A = 'RETIREMENT_PLAN_401A',
  RETIREMENT_PLAN_401K_FUND_REACH = 'RETIREMENT_PLAN_401K_FUND_REACH',
  RETIREMENT_PLAN_401K = 'RETIREMENT_PLAN_401K',
  RETIREMENT_PLAN_403 = 'RETIREMENT_PLAN_403',
  RETIREMENT_PLAN_457 = 'RETIREMENT_PLAN_457',
  RETIREMENT_PLAN_BENE_DUE_TO_DEATH = 'RETIREMENT_PLAN_BENE_DUE_TO_DEATH',
  RETIREMENT_PLAN_DECEASED = 'RETIREMENT_PLAN_DECEASED',
  RETIREMENT_PLAN = 'RETIREMENT_PLAN',
  ROLLOVER = 'ROLLOVER',
  ROLLOVER_IRA = 'ROLLOVER_IRA',
  ROTH_IRA_DECEASED_BENE = 'ROTH_IRA_DECEASED_BENE',
  ROTH_IRA_DECEASED = 'ROTH_IRA_DECEASED',
  ROTH_IRA = 'ROTH_IRA',
  ROTH_ROLLOVER_IRA = 'ROTH_ROLLOVER_IRA',
  SAR_SEP = 'SAR_SEP',
  SEP_IRA = 'SEP_IRA',
  SIM_401K = 'SIM_401K',
  SIMPLE_IRA = 'SIMPLE_IRA',
  TRADITIONAL_IRA_DECEASED_BENE = 'TRADITIONAL_IRA_DECEASED_BENE',
  TRADITIONAL_IRA_DECEASED = 'TRADITIONAL_IRA_DECEASED',
  TRADITIONAL_IRA = 'TRADITIONAL_IRA',
  TRANSFER_ON_DEATH = 'TRANSFER_ON_DEATH',
  TRUST = 'TRUST',
  UGMA_UTMA = 'UGMA_UTMA',
  ROTH_ROLLOVER = 'ROTH_ROLLOVER',
  NON_FIDUCIARY_PLAN = 'NON_FIDUCIARY_PLAN',
}

export const RECURRING_BUY_TRANSACTION_EDIT = 'RECURRING_BUY_TRANSACTION_EDIT';
export const TRANSACTION_LANDING = 'TRANSACTION_LANDING';

export const ADD_BANK = 'ADD_BANK';
export const ADD_SCHOOL_MODAL = 'ADD_SCHOOL_MODAL';
export const ADD_CHARITY_MODAL = 'ADD_CHARITY_MODAL';
export const DELETE_SCHOOL_MODAL = 'DELETE_SCHOOL_MODAL';
export const AUTOMATIC_LANDING = 'AUTOMATIC_LANDING';
export const BUY = 'BUY';
export const APP_BUY_TRANSACTION = 'APP_BUY_TRANSACTION';
export const APP_SELL_TRANSACTION = 'APP_SELL_TRANSACTION';
export const BUY_TRANSACTION = 'BUY_TRANSACTION';
export const COLLEGE_CONTRIBUTE = 'COLLEGE_CONTRIBUTE';
export const COLLEGE_CONTRIBUTE_TRANSACTION = 'COLLEGE_CONTRIBUTE_TRANSACTION';
export const COLLEGE_DISTRIBUTE = 'COLLEGE_DISTRIBUTE';
export const COLLEGE_DISTRIBUTE_TRANSACTION = 'COLLEGE_DISTRIBUTE_TRANSACTION';
export const COLLEGE_EXCHANGE = 'COLLEGE_EXCHANGE';
export const COLLEGE_EXCHANGE_TRANSACTION = 'COLLEGE_EXCHANGE_TRANSACTION';
export const DIST_TO_SCHOOL = 'DIST_TO_SCHOOL';
export const EXCHANGE = 'EXCHANGE';
export const EXCHANGE_TRANSACTION = 'EXCHANGE_TRANSACTION';
export const RECURRING_BUY = 'RECURRING_BUY';
export const RECURRING_BUY_TRANSACTION = 'RECURRING_BUY_TRANSACTION';
export const IRA_AUTOMAX_TRANSACTION = 'IRA_AUTOMAX_TRANSACTION';
export const RECURRING_SELL = 'RECURRING_SELL';
export const AUTOMATIC_SELL_TRANSACTION = 'AUTOMATIC_SELL_TRANSACTION';
export const RECURRING_EXCHANGE = 'RECURRING_EXCHANGE';
export const RECURRING_EXCHANGE_TRANSACTION_EDIT =
  'RECURRING_EXCHANGE_TRANSACTION_EDIT';
export const RECURRING_EXCHANGE_TRANSACTION = 'RECURRING_EXCHANGE_TRANSACTION';
export const SELL = 'SELL';
export const SELL_TRANSACTION = 'SELL_TRANSACTION';
export const IRA_AUTO_MAX = 'IRA_AUTO_MAX';
export const PURCHASE_NEW = 'PURCHASE NEW';
export const RMD = 'REQUIRED_MINIMUM_DISTRIBUTION';
export const DELETE_AUTO_MAX = 'DELETE_AUTO_MAX';

export enum TransactionTypes {
  BUY = 'Purchase',
  SELL = 'Redeem',
  EXCHANGE = 'Exchange',
  RECURRING_BUY = 'Purchase',
  DIST_TO_SCHOOL = 'DistributionToSchool',
  COLLEGE_CONTRIBUTE = 'Purchase', // same as buy
  COLLEGE_DISTRIBUTE = 'Redeem',
  COLLEGE_EXCHANGE = 'Exchange',
  RECURRING_EXCHANGE = 'Exchange',
  ROTH_CONVERSION = 'Exchange', // TO DO: check value
  AUTOMATIC_SELL = 'AutomaticTransactions',
}

export const TransactionTypeNameMap = {
  [BUY_TRANSACTION]: TransactionTypes[BUY],
  [SELL_TRANSACTION]: 'Redemption',
  [COLLEGE_CONTRIBUTE_TRANSACTION]: 'Contribution',
  [COLLEGE_DISTRIBUTE_TRANSACTION]: 'Distribution',
  [COLLEGE_EXCHANGE_TRANSACTION]: TransactionTypes[EXCHANGE],
  [EXCHANGE_TRANSACTION]: TransactionTypes[EXCHANGE],
  [RECURRING_BUY_TRANSACTION]: TransactionTypes[RECURRING_BUY],
  [RECURRING_EXCHANGE_TRANSACTION]: TransactionTypes[EXCHANGE],
};

export const chatTitle = {
  [BUY_TRANSACTION]: 'Guidance',
  [SELL_TRANSACTION]: 'Guidance',
  [COLLEGE_CONTRIBUTE_TRANSACTION]: 'Guidance',
  [COLLEGE_DISTRIBUTE_TRANSACTION]: 'Guidance',
  [EXCHANGE_TRANSACTION]: 'Guidance',
};

export enum UNIT {
  DOLLARS = 'DOLLAR',
  PERCENT = 'PERCENT',
  SHARES = 'SHARES',
  ALL_REMAINING = 'ALLREMAINING',
  ALL_SHARES = 'ALLSHARES',
}

export enum CountryCode {
  'US',
  'GU',
  'AS',
  'MP',
  'PR',
}

export enum US_STATES {
  AL = 'Alabama',
  AK = 'Alaska',
  AZ = 'Arizona',
  AR = 'Arkansas',
  CA = 'California',
  CO = 'Colorado',
  CT = 'Connecticut',
  DC = 'District of Columbia',
  DE = 'Delaware',
  FL = 'Florida',
  GA = 'Georgia',
  HI = 'Hawaii',
  ID = 'Idaho',
  IL = 'Illinois',
  IN = 'Indiana',
  IA = 'Iowa',
  KS = 'Kansas',
  KY = 'Kentucky',
  LA = 'Louisiana',
  ME = 'Maine',
  MD = 'Maryland',
  MA = 'Massachusetts',
  MI = 'Michigan',
  MN = 'Minnesota',
  MS = 'Mississippi',
  MZ = 'Missouri',
  MT = 'Montana',
  NB = 'Nebraska',
  NV = 'Nevada',
  NH = 'New Hampshire',
  NJ = 'New Jersey',
  NM = 'New Mexico',
  NY = 'New York',
  NC = 'North Carolina',
  ND = 'North Dakota',
  OH = 'Ohio',
  OK = 'Oklahoma',
  OR = 'Oregon',
  PA = 'Pennsylvania',
  RI = 'Rhode Island',
  SC = 'South Carolina',
  SD = 'South Dakota',
  TN = 'Tennessee',
  TX = 'Texas',
  UT = 'Utah',
  VT = 'Vermont',
  VA = 'Virginia',
  WA = 'Washington',
  WV = 'West Virginia',
  WI = 'Wisconsin',
  WY = 'Wyoming',
}

export enum US_STATE_CODE {
  AL = 'AL',
  AK = 'AK',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DC = 'DC',
  DE = 'DE',
  FL = 'FL',
  GA = 'GA',
  HI = 'HI',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  ME = 'ME',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MZ = 'MZ',
  MT = 'MT',
  NB = 'NB',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY',
}
//TODO: rename this array to be more business-specific
export const specialStates = [US_STATE_CODE.VT, US_STATE_CODE.CA];

export const selectableDays: ItemSelection[] = [
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
  { label: '7', value: 7 },
  { label: '8', value: 8 },
  { label: '9', value: 9 },
  { label: '10', value: 10 },
  { label: '11', value: 11 },
  { label: '12', value: 12 },
  { label: '13', value: 13 },
  { label: '14', value: 14 },
  { label: '15', value: 15 },
  { label: '16', value: 16 },
  { label: '17', value: 17 },
  { label: '18', value: 18 },
  { label: '19', value: 19 },
  { label: '20', value: 20 },
  { label: '21', value: 21 },
  { label: '22', value: 22 },
  { label: '23', value: 23 },
  { label: '24', value: 24 },
  { label: '25', value: 25 },
  { label: '26', value: 26 },
  { label: '27', value: 27 },
  { label: '28', value: 28 },
  { label: '29', value: 29 },
  { label: '30', value: 30 },
  { label: '31', value: 31 },
];

export enum Month {
  JAN = 'JAN',
  FEB = 'FEB',
  MAR = 'MAR',
  APR = 'APR',
  MAY = 'MAY',
  JUN = 'JUN',
  JUL = 'JUL',
  AUG = 'AUG',
  SEP = 'SEP',
  OCT = 'OCT',
  NOV = 'NOV',
  DEC = 'DEC',
}
export const selectableMonths: ItemSelection[] = [
  { label: Month.JAN, value: 0 },
  { label: Month.FEB, value: 1 },
  { label: Month.MAR, value: 2 },
  { label: Month.APR, value: 3 },
  { label: Month.MAY, value: 4 },
  { label: Month.JUN, value: 5 },
  { label: Month.JUL, value: 6 },
  { label: Month.AUG, value: 7 },
  { label: Month.SEP, value: 8 },
  { label: Month.OCT, value: 9 },
  { label: Month.NOV, value: 10 },
  { label: Month.DEC, value: 11 },
];

export enum cashLikeFunds {
  'PCTXX',
  'PRWCX',
  'PRFDX',
  'RPGAX',
  'RPGIX',
  'PRGMX',
  'PRIPX',
  'RPIBX',
  'PRITX',
  'PRJPX',
  'TRBFX',
  'PRNEX',
  'TRPBX',
  'PRSIX',
  'TQGEX',
  'TQMVX',
  'TRRGX',
  'TRRJX',
  'TRRDX',
  'TRRKX',
  'TRRMX',
  'TRRNX',
  'TRRLX',
  'PRWBX',
  'PSILX',
  'PRSMX',
  'TRROX',
  'TRRVX',
  'TRFOX',
  'TRFFX',
  'TRTFX',
  'PRFSX',
  'POMIX',
  'TRBUX',
  'TCBXX',
  'TRAIX',
  'REIPX',
  'TGAFX',
  'TRGAX',
  'PRXAX',
  'TIIPX',
  'RPISX',
  'PRIUX',
  'RJAIX',
  'TRLDX',
  'TRNEX',
  'TPPAX',
  'PPIPX',
  'TQGIX',
  'TQVIX',
  'TRFGX',
  'TRPJX',
  'TRPDX',
  'TRPKX',
  'TRPMX',
  'TRPNX',
  'TRPLX',
  'TBSIX',
  'TORFX',
  'TRVVX',
  'TOORX',
  'TRPPX',
  'TTOIX',
  'TTSIX',
  'TRSTX',
}

export enum RETIREMENT_DATED_FUNDS {
  'TTRRFX',
  'TRRAX',
  'TRRGX',
  'TRRBX',
  'TRRHX',
  'TRRCX',
  'TRRJX',
  'TRRDX',
  'TRRKX',
  'TRRMX',
  'TRRNX',
}

interface SupportedLanguageType {
  icon: string;
  label: string;
  value: LanguageCode;
}

export function supportedLanguages(
  translations: LanguageDefinition
): SupportedLanguageType[] {
  return [
    {
      icon: 'flag_usa.png',
      label: translations.languageEnglish,
      value: LanguageCode.EN_US,
    },
    {
      icon: 'flag_germany.png',
      label: translations.languageSpanish,
      value: LanguageCode.EN_MX,
    },
  ];
}

export enum linkFailures {
  InputError = 'INPUT ERROR',
  ConnectionError = 'CONNECTION ERROR',
  EventOriginError = 'EVENT ORIGIN ERROR',
  TimingError = 'TIMING ERROR',
  TokenError = 'TOKEN ERROR',
  ElementNotFoundError = 'ELEMENT NOT FOUND',
  EFI = 'EFI SYNC ERROR',
}

export const loadingBuySellAppMessages = [
  'Collecting Your Investment Information',
  'Calculating Your Balances',
  'Loading Your Banking Details',
];

export const loadingExchangeAppMessages = [
  'Collecting Your Investment Information',
  'Calculating Your Balance',
];

export const loadingExchangeCollegeMessages = [
  'Collecting Your Portfolio Information',
  'Calculating Your Balance',
];

export const loadingBuySellCollegeMessages = [
  'Collecting Your Portfolio Information',
  'Calculating Your Balances',
  'Loading Your Banking Details',
];

export const loadingAutomaticLandingMessages = [
  'Collecting Your Investment Information',
  'Loading Automatic Transactions',
];

export const executingBuyMessages = [
  'Processing Your Buy Request',
  'Connecting To Your Bank',
  'Confirming Your Transaction Details',
];

export const executingAppBuyMessages = [
  'Processing Your Add Request',
  'Connecting To Your Bank',
  'Confirming Your Transaction Details',
];

export const executingCollegeContributeMessages = [
  'Processing Your Contribution',
  'Connecting To Your Bank',
  'Confirming Your Contribution Details',
];

export const executingSellMessages = [
  'Processing Your Sell Request',
  'Confirming Your Transaction Details',
];

export const executingAppSellMessages = [
  'Processing Your Withdraw Request',
  'Confirming Your Transaction Details',
];

export const executingCollegeDistributeMessages = [
  'Processing Your Distribution',
  'Confirming Your Distribution Details',
];

export const executingExchangeMessages = [
  'Processing Your Exchange',
  'Confirming Your Transaction Details',
];

export const executingCollegeExchangeMessages = executingExchangeMessages;

export const executingAutomaticUpdateMessages = [
  'Processing Your Request',
  'Updating Your Information',
];

export const dailyMaximums = {
  college: {
    manual: {
      core: 250000,
      personal: 500000,
      enhanced_personal: 500000,
    },
    link: {
      core: 500000,
      personal: 500000,
      enhanced_personal: 500000,
    },
    manualJustAdded: {
      all: 100000,
    },
    transfer: {
      all: 100000,
    },
  },
  manual: {
    core: 999999.99,
    personal: 999999.99,
    enhanced_personal: 999999.99,
    ras: 500000,
  },
  link: {
    core: 999999.99,
    personal: 999999.99,
    enhanced_personal: 999999.99,
  },
  manualJustAdded: {
    all: 100000,
  },
  transfer: {
    all: 100000,
  },
  recurring: {
    buy: 25000,
    college_buy: 10000,
  },
};

export const recurringSweepMin = 100;

export const CORE_CUSTOMER = 'CORE';
export const MILLION_CUSTOMER = 'MIL';
export const PREFERED_CUSTOMER = 'PFD';
export const HIGH_NET_WORTH = 'HNW';
export const VIP_CUSTOMER = 'VIP';
export const NEW_1_CUSTOMER = 'NEW1';
export const NEW_2_CUSTOMER = 'NEW2';

export const CUSTOMER_SEGMENT_ENUM = {
  5: NEW_1_CUSTOMER,
  6: NEW_2_CUSTOMER,
  7: CORE_CUSTOMER,
  8: PREFERED_CUSTOMER,
  9: HIGH_NET_WORTH,
  10: MILLION_CUSTOMER,
  11: VIP_CUSTOMER,
};

export const APP_FAILURE = 'APP_FAILURE';
export const RELOAD_FAILURE = 'RELOAD_FAILURE';
export const PURCHASE_FAILURE = 'PURCHASE_FAILURE';
export const RECURRING_PURCHASE_FAILURE = 'RECURRING_PURCHASE_FAILURE';
export const PURCHASE_FAILURE_IRA_AUTOMAX = 'PURCHASE_FAILURE_IRA_AUTOMAX';
export const RECURRING_PURCHASE_APP_FAILURE = 'RECURRING_PURCHASE_APP_FAILURE';
export const TIMEOUT = 'TIMEOUT';
export const DOB_FAILURE = 'DOB_FAILURE';
export const ADDRESS_INCOMPLETE = 'ADDRESS_INCOMPLETE';
export const EXCHANGE_TO_EXISTING_LOAD_FAILED =
  'EXCHANGE_TO_EXISTING_LOAD_FAILED';
export const ZERO_BALANCE_FUND = 'ZERO_BALANCE_FUND';
export const ANY_STOP_TRANSFER = 'ANY_STOP_TRANSFER';
export const COLLEGE_CLIENT_ONLY = 'COLLEGE_CLIENT_ONLY';
export const COLLEGE_BUY_REDIRECT = 'COLLEGE_BUY_REDIRECT';
export const COLLEGE_EXCHANGE_REDIRECT = 'COLLEGE_EXCHANGE_REDIRECT';
export const COLLEGE_SELL_REDIRECT = 'COLLEGE_SELL_REDIRECT';
export const MD529_DECOMMISSION_ZERO_STATE = 'MD529_DECOMMISSION_ZERO_STATE';
export const NON_ELIGIBLE_AGREEMENTS = 'NON_ELIGIBLE_AGREEMENTS';
export const NOT_RETAIL_CLIENT = 'NOT_RETAIL_CLIENT';
export const FOREIGN_ADDRESS = 'FOREIGN_ADDRESS';
export const MD529_DECOMMISSION = 'MD529_DECOMMISSION';
export const ROTH_CONVERSION_NO_ELIGIBLE_FUNDS =
  'ROTH_CONVERSION_NO_ELIGIBLE_FUNDS';
export const ROTH_CONVERSION_NO_ELIGIBLE_BENES =
  'ROTH_CONVERSION_NO_ELIGIBLE_BENES';
export const TRAC_OR_RPS_ONLY = 'TRAC_OR_RPS_ONLY';
export const NO_ELIGIBLE_FUNDS_IRAS_IRA_AUTOMAX =
  'NO_ELIGIBLE_FUNDS_IRAS_IRA_AUTOMAX';
export const NO_IRA_AUTO_MAX_FUNDS = 'NO_IRA_AUTO_MAX_FUNDS';
export const QCD_ZERO_STATE = 'QCD_ZERO_STATE';
export const CUSTODIAN_ONLY = 'CUSTODIAN_ONLY';
export enum SystemCode {
  STOP_MAIL_ID = 34,
  STOP_TRANSFER_ID = 31,
  WIRE_REDEMPTION = 55,
  CHECK_REDEMPTION = 20,
  ACH_REDEMPTION = 49,
  AUTOMATED_SYSTEMS_STOP_ALL = 41,
  AUTOMATED_SYSTEMS_STOP_BALANCE = 42,
  AUTOMATED_SYSTEMS_STOP_CHECK = 43,
  AUTOMATED_SYSTEMS_STOP_EXCHANGE = 44,
  AUTOMATED_SYSTEMS_STOP_PURCHASE = 45,
  AUTOMATED_SYSTEMS_STOP_REDEMPTION_ID = 46,

  DIVIDEND_MOVE = 18,
  DIVIDEND_CHECK_TO_ALTERNATIVE_ADDRESS = 17,
  EXCHANGE_REQUIRED_FEATURE = 19,
  EXCHANGE_REQUIRED_FEATURE_CHECK = 20,
  PENALTY_WITHOLDING = 32,
  RETURN_MAIL_VERIFY_ADDRESS = 34,
  STOP_PURCHASE = 30,
  STOP_BALANCE = 42,
  STOP_CHECK = 43,
  WSI_STOP_EXCHANGE = 'VRUSTEXG',
  SYSTEMATIC_EXCHANGE_FROM_ACCOUNT = 51,
  SYSTEMATIC_EXCHANGE_TO_ACCOUNT = 52,
  SYSTEMATIC_PURCHASE_TO_ACCOUNT = 53,
  CONFIRM_TO_INTRSTD_PARTY_WARNING = 14,
  ACH_DIVIDEND = 47,
  RETURN_E_MAIL_VERIFY_ADDRESS = 88,
}

export enum SalesforceIgnoredSystemCodes {
  AUTOMATED_SYSTEMS_STOP_EXCHANGE = SystemCode.AUTOMATED_SYSTEMS_STOP_EXCHANGE,
  AUTOMATED_SYSTEMS_STOP_PURCHASE = SystemCode.AUTOMATED_SYSTEMS_STOP_PURCHASE,
  AUTOMATED_SYSTEMS_STOP_REDEMPTION_ID = SystemCode.AUTOMATED_SYSTEMS_STOP_REDEMPTION_ID,
}

export const EXPRESS_DELIVERY_CORE_FEE = 5;
export const EXPRESS_DELIVERY_MIN_TRANSACTION = 5000;
export const DELIVERY_MAIL_CHECK_REDEMPTION = 250000;
export const COLLEGE_DISTRIBUTE_MAX_REDEMPTION = 99999.99;
export const MD529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION = 49999.99;
export const NAT529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION = 99999.99;
export const AK529_COLLEGE_DISTRIBUTE_MAX_REDEMPTION = 99999.99;
export const MAXIMUM_ACH_REDEMPTION = 100000;
export const MAXIMUM_CHECK_REDEMPTION = 250000;
export const MAXIMUM_WIRE_REDEMPTION = 100000;
export const MAX_CHARITY_DONATION_MAIL_TO_CHARITY = isYearAfter2024()
  ? 108000
  : 105000;
export const MAX_CHARITY_DONATION_MAIL_TO_ME = isYearAfter2024()
  ? 108000
  : 105000;
export const MIN_CHARITY_DONATION = 1;

export enum ExchangeType {
  ONE_TO_ONE = 'EXCHANGE_TYPE_ONE_TO_ONE',
  ONE_TO_MANY = 'EXCHANGE_TYPE_ONE_TO_MANY',
  MANY_TO_ONE = 'EXCHANGE_TYPE_MANY_TO_ONE',
  RECURRING = 'EXCHANGE_TYPE_RECURRING',
}

export const LINK_BANK = 'LINK_BANK';
export const EVERIFY = 'EVERIFY';
export const MANUAL_ENTRY = 'MANUAL_ENTRY';
export const BANK_SELECTION = 'BANK_SELECTION';
export const BANK_SUCCESS = 'BANK_SUCCESS';
export const BANK_SUCCESS_MANUAL = 'BANK_SUCCESS_MANUAL';
export const BANK_FAILURE = 'BANK_FAILURE';

export const INIT = 'INIT';
export const LANDING = 'LANDING';
export const LINK = 'LINK';
export const MANUAL = 'MANUAL';

export const addBankAnalyticsMap = {
  [LINK_BANK]: 'Choose Bank',
  [EVERIFY]: 'ManualBankEverify',
  [LANDING]: 'Add New Bank Landing Page',
  [MANUAL_ENTRY]: 'Manual Bank Account Details',
  [BANK_FAILURE]: 'Add Bank Failure', // This can be overridden to be quovo or everify
  [BANK_SELECTION]: 'Choose Bank Account',
  [BANK_SUCCESS]: 'Bank Success',
  [BANK_SUCCESS_MANUAL]: 'Bank Success Manual',
};

export enum CostBasisMethodType {
  FIRST_IN_FIRST_OUT = 'FIRST_IN_FIRST_OUT',
  LAST_IN_FIRST_OUT = 'LAST_IN_FIRST_OUT',
  HIGH_COST = 'HIGH_COST',
  HIGH_COST_LONG_TERM = 'HIGH_COST_LONG_TERM',
  HIGH_COST_SHORT_TERM = 'HIGH_COST_SHORT_TERM',
  LOW_COST = 'LOW_COST',
  LOW_COST_LONG_TERM = 'LOW_COST_LONG_TERM',
  LOW_COST_SHORT_TERM = 'LOW_COST_SHORT_TERM',
  AVERAGE_COST = 'AVERAGE_COST',
  SPECIFIC_LOT_IDENTIFICATION = 'SPECIFIC_LOT_IDENTIFICATION',
  LOSS_GAIN_UTILIZATION = 'LOSS_GAIN_UTILIZATION',
  MINIMIZED_SHORT_TERM_GAINS = 'MINIMIZED_SHORT_TERM_GAINS',
}

export const COST_BASIS_ENUM = {
  1: CostBasisMethodType.FIRST_IN_FIRST_OUT,
  2: CostBasisMethodType.LAST_IN_FIRST_OUT,
  3: CostBasisMethodType.HIGH_COST,
  4: CostBasisMethodType.HIGH_COST_LONG_TERM,
  5: CostBasisMethodType.HIGH_COST_SHORT_TERM,
  6: CostBasisMethodType.LOW_COST,
  7: CostBasisMethodType.LOW_COST_LONG_TERM,
  8: CostBasisMethodType.LOW_COST_SHORT_TERM,
  10: CostBasisMethodType.AVERAGE_COST,
  11: CostBasisMethodType.SPECIFIC_LOT_IDENTIFICATION,
  12: CostBasisMethodType.LOSS_GAIN_UTILIZATION,
  13: CostBasisMethodType.MINIMIZED_SHORT_TERM_GAINS,
};

export const COST_BASIS_DISPLAY_MAP = {
  [CostBasisMethodType.FIRST_IN_FIRST_OUT]: 'First In First Out',
  [CostBasisMethodType.LAST_IN_FIRST_OUT]: 'Last In First Out',
  [CostBasisMethodType.HIGH_COST]: 'High Cost',
  [CostBasisMethodType.HIGH_COST_LONG_TERM]: 'High Cost Long-Term',
  [CostBasisMethodType.HIGH_COST_SHORT_TERM]: 'High Cost Short-Term',
  [CostBasisMethodType.LOW_COST]: 'Low Cost',
  [CostBasisMethodType.LOW_COST_LONG_TERM]: 'Low Cost Long-Term',
  [CostBasisMethodType.LOW_COST_SHORT_TERM]: 'Low Cost Short-Term',
  [CostBasisMethodType.AVERAGE_COST]: 'Average Cost',
  [CostBasisMethodType.SPECIFIC_LOT_IDENTIFICATION]:
    'Specific Lot Identification',
  [CostBasisMethodType.LOSS_GAIN_UTILIZATION]: 'Loss/Gain Utilization',
  [CostBasisMethodType.MINIMIZED_SHORT_TERM_GAINS]:
    'Minimized Short Term Gains',
};

export const addNewFundAnalyticsMap = {
  REGISTRATION: 'Select New Registration',
  FUND_SEARCH: 'New Fund Search',
};

export const bankFailureMessages = {
  MANUAL: {
    messages: {
      success: {
        check: {
          headline: `Check Your Inbox`,
          msg: `Over the next 2-3 business days you will receive an email requesting verification of micro-deposits into your chosen bank account.`,
        },
        review: {
          headline: `Review Micro-Deposits`,
          msg: `Log in to your bank and review the micro-deposit transactions.`,
        },
        complete: {
          headline: `Complete Linking Your Bank`,
          msg: `Follow the instructions in your email from T. Rowe Price to successfully link your bank to all eligible funds.`,
        },
      },
    },
  },
  LINK: {
    messages: {
      failure: {
        denial: {
          off_ramp: {
            isPDF: true,
            PDF: {
              text: 'Please fill out the Account Services form',
              destination:
                'https://individual.troweprice.com/staticFiles/Retail/Shared/PDFs/shrhldrsrvcs.pdf',
            },
          },
        },
      },
      success: {
        generic: {
          headline: `Success!`,
          msg: `This bank will be linked to all eligible funds, and is available to use for purchases immediately.`,
        },
      },
    },
  },
};

export const AVAILABLE_CONTRIBUTION_TIP_TEXT =
  'IRA contributions and remaining contribution limits do not include Workplace Retirement accounts.';

export const EARLY_WITHDRAWAL_PENALTY_AGE = 59.5;
export const FUND_LIMIT_MESSAGE_DELIMITER = '/';
export const FUND_LOCK_CHECK_REDEMPTION_CODE = 20;
export const APP_MINIMUM_BALANCE = 10000;

export enum FederalTaxPercent {
  MIN = 0,
  MAX = 99,
  DEFAULT = 10,
}

export enum FundNoteAdd {
  OWNED_EXISTING = 'ADD_FUND_NOTE_OWNED_EXISTING',
  OWNED_NEW = 'ADD_FUND_NOTE_OWNED_NEW',
  RETIREMENT_INCOME_FOR_IRA = 'ADD_FUND_NOTE_RETIREMENT_INCOME_FOR_IRA',
  RETIREMENT_INCOME_FOR_NON_IRA = 'ADD_FUND_NOTE_RETIREMENT_INCOME_FOR_NON_IRA',
  SUMMIT = 'ADD_FUND_NOTE_SUMMIT',
}

export enum FundNoteBuy {
  ADDRESS_UNDELIVERABLE = 'BUY_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  APP_ZERO_DOLLAR_BALANCE = 'APP_ZERO_DOLLAR_BALANCE',
  AGE_RESTRICTED = 'BUY_FUND_NOTE_AGE_RESTRICTED',
  BANK_NOT_LINKED = 'BUY_FUND_NOTE_BANK_NOT_LINKED',
  BUCKET_ROLL = 'BUY_FUND_NOTE_BUCKET_ROLL',
  CANNOT_LINK_BANK = 'BUY_FUND_NOTE_CANNOT_LINK_BANK',
  CANNOT_REOPEN_ICLASS = 'BUY_FUND_NOTE_CANNOT_REOPEN_ICLASS',
  CANNOT_REOPEN_SUMMIT = 'BUY_FUND_NOTE_CANNOT_REOPEN_SUMMIT',
  CANNOT_PURCHASE_2020 = 'BUY_FUND_NOTE_CANNOT_PURCHASE_2020',
  CLOSED_NEW = 'BUY_FUND_NOTE_CLOSED_NEW',
  COUNTRY_RESTRICTED = 'BUY_FUND_NOTE_COUNTRY_RESTRICTED',
  ESA_PLAN = 'BUY_FUND_NOTE_ESA_PLAN',
  INHERITED_FUND = 'BUY_FUND_NOTE_INHERITED_FUND',
  IRA_DECEASED = 'BUY_FUND_NOTE_IRA_DECEASED',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'BUY_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'BUY_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'BUY_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'BUY_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  ONLINE_PURCHASE_RESTRICTED = 'BUY_FUND_NOTE_ONLINE_PURCHASE_RESTRICTED',
  PENDING = 'BUY_FUND_NOTE_PENDING',
  PENDING_STATUS = 'PENDING_STATUS_AGREEMENTS',
  RECENTLY_REDEEMED = 'BUY_FUND_NOTE_RECENTLY_REDEEMED',
  STOP_PURCHASE = 'BUY_FUND_NOTE_STOP_PURCHASE',
  EXISTING_ICLASS = 'EXISTING_ICLASS',
  PENALTY_WITHHOLDING = 'BUY_PENALTY_WITHHOLDING',
}
export enum FundNoteAutoBuy {
  AUTO_BUY = 'RECURRING_BUY_FUND_NOTE_AUTO_BUY',
  AUTO_EXCHANGE = 'RECURRING_BUY_FUND_NOTE_AUTO_EXCHANGE',
  AUTO_SELL = 'RECURRING_BUY_FUND_NOTE_AUTO_SELL',
  BANK_NOT_LINKED = 'RECURRING_BUY_FUND_NOTE_BANK_NOT_LINKED',
  CLOSED_FUND = 'RECURRING_BUY_FUND_NOTE_CLOSED_FUND',
  IRA_AUTOMAX = 'RECURRING_BUY_FUND_NOTE_IRA_AUTOMAX',
  PENDING = 'RECURRING_BUY_FUND_NOTE_PENDING',
  RETURN_E_MAIL_VERIFY_ADDRESS = 'RECURRING_BUY_FUND_NOTE_RETURN_E_MAIL_VERIFY_ADDRESS',
  STOP_PURCHASE = 'RECURRING_BUY_FUND_NOTE_STOP_PURCHASE',
}

export enum FundNoteAutoExchange {
  AUTO_BUY = 'RECURRING_EXCHANGE_FUND_NOTE_AUTO_BUY',
  AUTO_SELL = 'RECURRING_EXCHANGE_FUND_NOTE_AUTO_SELL',
  AUTO_EXCHANGE = 'RECURRING_EXCHANGE_FUND_NOTE_AUTO_EXCHANGE',
  AUTO_EXCHANGE_TO = 'RECURRING_EXCHANGE_TO_FUND_NOTE_AUTO_EXCHANGE',
  AUTOMATED_SYSTEMS_STOP_ALL = 'RECURRING_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_ALL',
  AUTOMATED_SYSTEMS_STOP_BALANCE = 'RECURRING_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_BALANCE',
  AUTOMATED_SYSTEMS_STOP_CHECK = 'RECURRING_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_CHECK',
  AUTOMATED_SYSTEMS_STOP_PURCHASE = 'RECURRING_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_PURCHASE',
  AUTOMATED_SYSTEMS_STOP_EXCHANGE = 'RECURRING_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_EXCHANGE',
  COUNTRY_RESTRICTED = 'RECURRING_EXCHANGE_FUND_NOTE_COUNTRY_RESTRICTED',
  CLOSED_FUND = 'RECURRING_EXCHANGE_FUND_NOTE_CLOSED_FUND',
  CLOSED_NEW = 'RECURRING_EXCHANGE_FUND_NOTE_CLOSED_NEW',
  ESA_PLAN = 'RECURRING_EXCHANGE_FUND_NOTE_ESA_PLAN',
  FID_NON_FID = 'RECURRING_EXCHANGE_FUND_NOTE_FID_NON_FID',
  FROM_ZERO = 'RECURRING_EXCHANGE_FUND_NOTE_FROM_ZERO',
  INHERITED_FUND = 'RECURRING_EXCHANGE_FUND_NOTE_INHERITED_FUND',
  IRA_AUTOMAX = 'RECURRING_EXCHANGE_FUND_NOTE_IRA_AUTOMAX',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'RECURRING_EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'RECURRING_EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'RECURRING_EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'RECURRING_EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  NONRETIREMENT_TO_RETIREMENT = 'RECURRING_EXCHANGE_FUND_NOTE_NONRETIREMENT_TO_RETIREMENT',
  NOT_ELIGIBLE = 'EXCHANGE_FUND_NOTE_NOT_ELIGIBLE',
  ONLINE_PURCHASE_RESTRICTED = 'RECURRING_EXCHANGE_FUND_NOTE_ONLINE_PURCHASE_RESTRICTED',
  PENALTY_WITHHOLDING = 'RECURRING_EXCHANGE_FUND_NOTE_PENALTY_WITHHOLDING',
  PENDING = 'RECURRING_EXCHANGE_FUND_NOTE_PENDING',
  PENDING_TRANSACTION = 'RECURRING_EXCHANGE_FUND_NOTE_PENDING_TRANSACTION',
  PURCHASED_BLOCKED = 'RECURRING_EXCHANGE_FUND_NOTE_PURCHASED_BLOCKED',
  RETURN_MAIL_VERIFY_ADDRESS = 'RECURRING_EXCHANGE_FUND_NOTE_RETURN_MAIL_VERIFY_ADDRESS',
  STOP_PURCHASE = 'RECURRING_EXCHANGE_FUND_NOTE_STOP_PURCHASE',
  STOP_TRANSFER = 'RECURRING_EXCHANGE_FUND_NOTE_STOP_TRANSFER',
  TAXABLE_TO_IRA = 'RECURRING_EXCHANGE_FUND_NOTE_TAXABLE_TO_IRA',
  UNAVAILABLE = 'RECURRING_EXCHANGE_FUND_NOTE_UNAVAILABLE',
  NON_IRA_MATCHING = 'RECURRING_EXCHANGE_NON_IRA_MATCHING',
  IRA_MATCHING = 'RECURRING_EXCHANGE_IRA_MATCHING',
  EXISTING_RMD = 'EXISTING_RMD',
}

export enum FundNoteSell {
  ADDRESS_CHANGE_PENDING = 'SELL_FUND_NOTE_ADDRESS_CHANGE_PENDING',
  ADDRESS_UNDELIVERABLE = 'SELL_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  AUTOMATED_SYSTEMS_STOP_REDEMPTION = 'SELL_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_REDEMPTION',
  BANK_MISSING_ACH_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ACH_SERVICE_FEATURES',
  BANK_MISSING_ACH_MAIL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ACH_MAIL_SERVICE_FEATURES',
  BANK_MISSING_ALL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ALL_SERVICE_FEATURES',
  BANK_MISSING_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANk_MISSING_SERVICE_FEATURES',
  BANK_MISSING_WIRE_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_WIRE_SERVICE_FEATURES',
  BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES',
  BANK_NOT_LINKED = 'SELL_FUND_NOTE_BANK_NOT_LINKED',
  CANNOT_LINK_BANK = 'SELL_FUND_NOTE_CANNOT_LINK_BANK',
  CANNOT_SELL_2020 = 'SELL_FUND_NOTE_CANNOT_SELL_2020',
  ESA_PLAN = 'SELL_FUND_NOTE_ESA_PLAN',
  INELIGIBLE_BALANCE = 'SELL_FUND_NOTE_INELIGIBLE_BALANCE',
  INHERITED_FUND = 'SELL_FUND_NOTE_INHERITED_FUND',
  IRA_DECEASED = 'SELL_FUND_NOTE_IRA_DECEASED',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  NON_FIDUCIARY_FUND = 'SELL_FUND_NOTE_NON_FIDUCIARY_FUND',
  PENDING = 'SELL_FUND_NOTE_PENDING',
  PENDING_STATUS = 'SELL_PENDING_STATUS_AGREEMENTS',
  PENDING_TRANSACTION = 'SELL_FUND_NOTE_PENDING_TRANSACTION',
  SEP_IRA = 'SELL_FUND_NOTE_SEP_IRA',
  STOP_TRANSFER = 'SELL_FUND_NOTE_STOP_TRANSFER',
  UNCOLLECTED_SHARES = 'SELL_FUND_NOTE_UNCOLLECTED_SHARES',
  UNAVAILABLE_DELIVERY = 'SELL_FUND_NOTE_UNAVAILABLE_DELIVERY',
  UNAVAILABLE_FOR_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_REDEMPTION_ONLINE',
  UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE',
  UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE',
  ZERO_BALANCE_WITH_UNCOLLECTED_SHARES = 'SELL_FUND_NOTE_ZERO_BALANCE_WITH_UNCOLLECTED_SHARES',
  PENALTY_WITHHOLDING = 'SELL_FUND_NOTE_PENALTY_WITHHOLDING',
  NO_RAS_IN_KIND_TRANSFER = 'SELL_FUND_NOTE_NO_RAS_IN_KIND_TRANSFER',
  RAS_EARLY_IRA_WITHDRAWAL = 'SELL_FUND_NOTE_RAS_EARLY_IRA_WITHDRAWAL',
}
export enum FundNoteQCD {
  ADDRESS_CHANGE_PENDING = 'SELL_FUND_NOTE_ADDRESS_CHANGE_PENDING',
  ADDRESS_UNDELIVERABLE = 'SELL_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  AUTOMATED_SYSTEMS_STOP_REDEMPTION = 'SELL_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_REDEMPTION',
  BANK_MISSING_ACH_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ACH_SERVICE_FEATURES',
  BANK_MISSING_ACH_MAIL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ACH_MAIL_SERVICE_FEATURES',
  BANK_MISSING_ALL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_ALL_SERVICE_FEATURES',
  BANK_MISSING_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANk_MISSING_SERVICE_FEATURES',
  BANK_MISSING_WIRE_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_WIRE_SERVICE_FEATURES',
  BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES = 'SELL_FUND_NOTE_BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES',
  BANK_NOT_LINKED = 'SELL_FUND_NOTE_BANK_NOT_LINKED',
  CANNOT_LINK_BANK = 'SELL_FUND_NOTE_CANNOT_LINK_BANK',
  CANNOT_SELL_2020 = 'SELL_FUND_NOTE_CANNOT_SELL_2020',
  ESA_PLAN = 'SELL_FUND_NOTE_ESA_PLAN',
  INELIGIBLE_BALANCE = 'SELL_FUND_NOTE_INELIGIBLE_BALANCE',
  INHERITED_FUND = 'SELL_FUND_NOTE_INHERITED_FUND',
  IRA_DECEASED = 'SELL_FUND_NOTE_IRA_DECEASED',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'SELL_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  NON_FIDUCIARY_FUND = 'SELL_FUND_NOTE_NON_FIDUCIARY_FUND',
  PENDING = 'SELL_FUND_NOTE_PENDING',
  PENDING_STATUS = 'SELL_PENDING_STATUS_AGREEMENTS',
  PENDING_TRANSACTION = 'SELL_FUND_NOTE_PENDING_TRANSACTION',
  SEP_IRA = 'SELL_FUND_NOTE_SEP_IRA',
  STOP_TRANSFER = 'SELL_FUND_NOTE_STOP_TRANSFER',
  UNCOLLECTED_SHARES = 'SELL_FUND_NOTE_UNCOLLECTED_SHARES',
  UNAVAILABLE_FOR_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_REDEMPTION_ONLINE',
  UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE',
  UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE = 'SELL_FUND_NOTE_UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE',
  ZERO_BALANCE_WITH_UNCOLLECTED_SHARES = 'SELL_FUND_NOTE_ZERO_BALANCE_WITH_UNCOLLECTED_SHARES',
  PENALTY_WITHHOLDING = 'SELL_FUND_NOTE_PENALTY_WITHHOLDING',
  NO_RAS_IN_KIND_TRANSFER = 'SELL_FUND_NOTE_NO_RAS_IN_KIND_TRANSFER',
  RAS_EARLY_IRA_WITHDRAWAL = 'SELL_FUND_NOTE_RAS_EARLY_IRA_WITHDRAWAL',
  QCD_INHERITED_IRA = 'QCD_INHERITED_IRA',
}

export enum FundNoteRothConversion {
  ADDRESS_UNDELIVERABLE = 'ROTH_CONVERSION_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  AUTOMATED_SYSTEMS_STOP_REDEMPTION = 'ROTH_CONVERSION_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_REDEMPTION',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'ROTH_CONVERSION_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'ROTH_CONVERSION_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'ROTH_CONVERSION_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'ROTH_CONVERSION_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  PENDING = 'ROTH_CONVERSION_FUND_NOTE_PENDING',
  PENDING_TRANSACTION = 'ROTH_CONVERSION_FUND_NOTE_PENDING_TRANSACTION',
  PENDING_FROM_TRANSACTION = 'ROTH_CONVERSION_FUND_NOTE_PENDING_FROM_TRANSACTION',
  STOP_TRANSFER = 'ROTH_CONVERSION_FUND_NOTE_STOP_TRANSFER',
  UNCOLLECTED_SHARES = 'ROTH_CONVERSION_FUND_NOTE_UNCOLLECTED_SHARES',
  ZERO_BALANCE_WITH_UNCOLLECTED_SHARES = 'ROTH_CONVERSION_FUND_NOTE_ZERO_BALANCE_WITH_UNCOLLECTED_SHARES',
  AUTOMATED_SYSTEMS_STOP_ALL = 'ROTH_CONVERSION_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_ALL',
  CONVERSION_RESTRICTED_RETIREMENT_2020 = 'CONVERSION_RESTRICTED_RETIREMENT_2020',
  ROTH_RECENTLY_REDEEMED = 'ROTH_RECENTLY_REDEEMED',
  ROTH_STOP_PURCHASE = 'ROTH_STOP_PURCHASE',
  EXISTING_ICLASS = 'EXISTING_ICLASS',
}

export enum FundNoteDistributeCollege {
  ADDRESS_CHANGE_PENDING = 'COLLEGE_DISTRIBUTE_FUND_NOTE_ADDRESS_CHANGE_PENDING',
  ADDRESS_UNDELIVERABLE = 'COLLEGE_DISTRIBUTE_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  AK529_ACT = 'COLLEGE_DISTRIBUTE_FUND_NOTE_AK529_ACT',
  BANK_NOT_LINKED = 'COLLEGE_DISTRIBUTE_FUND_NOTE_BANK_NOT_LINKED',
  CANNOT_LINK_BANK = 'COLLEGE_DISTRIBUTE_FUND_NOTE_CANNOT_LINK_BANK',
  CHECK_DISTRIBUTION_UNAVAILABLE = 'COLLEGE_DISTRIBUTE_FUND_NOTE_CHECK_DISTRIBUTION_UNAVAILABLE',
  DIRECT_DEPOSIT_UNAVAILABLE = 'COLLEGE_DISTRIBUTE_FUND_NOTE_DIRECT_DEPOSIT_UNAVAILABLE',
  STOP_TRANSFER = 'COLLEGE_DISTRIBUTE_FUND_NOTE_STOP_TRANSFER',
  TRANSACTION_PENDING = 'COLLEGE_DISTRIBUTE_FUND_NOTE_TRANSACTION_PENDING',
  TRANSACTION_PENDING_ALL = 'COLLEGE_DISTRIBUTE_FUND_NOTE_TRANSACTION_PENDING_ALL',
  UNAVAILABLE_ONLINE = 'COLLEGE_DISTRIBUTE_FUND_NOTE_UNAVAILABLE_ONLINE',
  UNCOLLECTED_UNITS = 'COLLEGE_DISTRIBUTE_FUND_NOTE_UNCOLLECTED_UNITS',
  PENDING_STATUS = 'COLLEGE_PENDING_STATUS_AGREEMENTS',
  WIRE_REDEMPTION_UNAVAILABLE = 'COLLEGE_DISTRIBUTE_FUND_NOTE_WIRE_REDEMPTION_UNAVAILABLE',
  ONE_BENE_PER_DTS = 'COLLEGE_DISTRIBUTE_FUND_NOTE_ONE_BENE_PER_DTS',
}

export enum FundNoteExchange {
  ACH_DIVIDEND = 'EXCHANGE_FUND_NOTE_ACH_DIVIDEND',
  ADDRESS_UNDELIVERABLE = 'EXCHANGE_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  AGE_RESTRICTED = 'EXCHANGE_FUND_NOTE_AGE_RESTRICTED',
  AUTOMATED_SYSTEMS_STOP_ALL = 'EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_ALL',
  AUTOMATED_SYSTEMS_STOP_BALANCE = 'EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_BALANCE',
  AUTOMATED_SYSTEMS_STOP_CHECK = 'EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_CHECK',
  AUTOMATED_SYSTEMS_STOP_PURCHASE = 'EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_PURCHASE',
  BUCKET_ROLL = 'EXCHANGE_FUND_NOTE_BUCKET_ROLL',
  FROM_AUTOMATED_SYSTEMS_STOP_EXCHANGE = 'EXCHANGE_FUND_NOTE_FROM_AUTOMATED_SYSTEMS_STOP_EXCHANGE',
  CANNOT_LINK_BANK = 'EXCHANGE_FUND_NOTE_CANNOT_LINK_BANK',
  CLOSED_NEW = 'EXCHANGE_FUND_NOTE_CLOSED_NEW',
  COUNTRY_RESTRICTED = 'EXCHANGE_FUND_NOTE_COUNTRY_RESTRICTED',
  ESA_PLAN = 'EXCHANGE_FUND_NOTE_ESA_PLAN',
  FROM_ZERO = 'EXCHANGE_FUND_NOTE_FROM_ZERO',
  INELIGIBLE_BALANCE = 'EXCHANGE_FUND_NOTE_INELIGIBLE_BALANCE',
  INHERITED_FUND = 'EXCHANGE_FUND_NOTE_INHERITED_FUND',
  MONEY_MARKET_EVENT_FEE_ACTIVE = 'EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_FEE_ACTIVE',
  MONEY_MARKET_EVENT_FEE_REMOVED = 'EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_FEE_REMOVED',
  MONEY_MARKET_EVENT_GATE_ACTIVE = 'EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_GATE_ACTIVE',
  MONEY_MARKET_EVENT_GATE_REMOVED = 'EXCHANGE_FUND_NOTE_MONEY_MARKET_EVENT_GATE_REMOVED',
  NOT_ELIGIBLE = 'EXCHANGE_FUND_NOTE_NOT_ELIGIBLE',
  REQUIRES_CONVERSION = 'EXCHANGE_FUND_NOTE_REQUIRES_CONVERSION',
  FIDUCIARY_TYPE = 'EXCHANGE_FUND_NOTE_FIDUCIARY_TYPE',
  ONLINE_PURCHASE_RESTRICTED = 'EXCHANGE_FUND_NOTE_ONLINE_PURCHASE_RESTRICTED',
  PENALTY_WITHHOLDING = 'EXCHANGE_FUND_NOTE_PENALTY_WITHHOLDING',
  PENDING = 'EXCHANGE_FUND_NOTE_PENDING',
  PENDING_TRANSACTION = 'EXCHANGE_FUND_NOTE_PENDING_TRANSACTION',
  PURCHASED_BLOCKED = 'EXCHANGE_FUND_NOTE_PURCHASED_BLOCKED',
  RECENTLY_REDEEMED = 'EXCHANGE_FUND_NOTE_RECENTLY_REDEEMED',
  RETURN_MAIL_VERIFY_ADDRESS = 'EXCHANGE_FUND_NOTE_RETURN_MAIL_VERIFY_ADDRESS',
  STOP_PURCHASE = 'EXCHANGE_FUND_NOTE_STOP_PURCHASE',
  STOP_TRANSFER = 'EXCHANGE_FUND_NOTE_STOP_TRANSFER',
  TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE = 'EXCHANGE_FUND_NOTE_TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE',
  UNAVAILABLE = 'EXCHANGE_FUND_NOTE_UNAVAILABLE',
  UNAVAILABLE_FOR_REDEMPTION_ONLINE = 'EXCHANGE_FUND_NOTE_UNAVAILABLE_FOR_REDEMPTION_ONLINE',
  WITHIN_CURRENT_INVESTMENTS = 'EXCHANGE_WITHIN_CURRENT_INVESTMENTS',
  EXCHANGE_UNAVAILABLE = 'EXCHANGE_UNAVAILABLE',
  EXISTING_ICLASS = 'EXISTING_ICLASS',
}

export enum FundNoteCollege {
  ADDRESS_UNDELIVERABLE = 'COLLEGE_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  CANNOT_LINK_BANK = 'COLLEGE_FUND_NOTE_CANNOT_LINK_BANK',
  COUNTRY_RESTRICTED = 'COLLEGE_FUND_NOTE_COUNTRY_RESTRICTED',
  ONLINE_PURCHASE_RESTRICTED = 'COLLEGE_FUND_NOTE_ONLINE_PURCHASE_RESTRICTED',
  PENDING = 'COLLEGE_FUND_NOTE_PENDING',
  PENDING_STATUS = 'COLLEGE_PENDING_STATUS_AGREEMENTS',
  STOP_PURCHASE = 'COLLEGE_FUND_NOTE_STOP_PURCHASE',
}

export enum FundNoteExchangeCollege {
  ADDRESS_UNDELIVERABLE = 'COLLEGE_EXCHANGE_FUND_NOTE_ADDRESS_UNDELIVERABLE',
  ALASKA_ACT_RESTRICTION = 'COLLEGE_EXCHANGE_FUND_NOTE_ALASKA_ACT_RESTRICTION',
  AUTOMATED_SYSTEMS_STOP_ALL = 'COLLEGE_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_ALL',
  AUTOMATED_SYSTEMS_STOP_CHECK = 'COLLEGE_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_CHECK',
  AUTOMATED_SYSTEMS_STOP_EXCHANGE = 'COLLEGE_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_EXCHANGE',
  AUTOMATED_SYSTEMS_STOP_PURCHASE = 'COLLEGE_EXCHANGE_FUND_NOTE_AUTOMATED_SYSTEMS_STOP_PURCHASE',
  CLOSED_NEW = 'COLLEGE_EXCHANGE_FUND_NOTE_CLOSED_NEW',
  COUNTRY_RESTRICTED = 'COLLEGE_EXCHANGE_FUND_NOTE_COUNTRY_RESTRICTED',
  FROM_ZERO = 'COLLEGE_EXCHANGE_FUND_NOTE_FROM_ZERO',
  PENDING = 'COLLEGE_EXCHANGE_FUND_NOTE_PENDING',
  PENDING_TRANSACTION = 'COLLEGE_EXCHANGE_FUND_NOTE_PENDING_TRANSACTION',
  RETURN_MAIL_VERIFY_ADDRESS = 'COLLEGE_EXCHANGE_FUND_NOTE_RETURN_MAIL_VERIFY_ADDRESS',
  STOP_PURCHASE = 'COLLEGE_EXCHANGE_FUND_NOTE_STOP_PURCHASE',
  STOP_TRANSFER = 'COLLEGE_EXCHANGE_FUND_NOTE_STOP_TRANSFER',
  TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE = 'COLLEGE_EXCHANGE_FUND_NOTE_TO_AUTOMATED_SYSTEMS_STOP_EXCHANGE',
  TWO_EXCHANGES_PER_YEAR = 'COLLEGE_EXCHANGE_FUND_NOTE_TWO_EXCHANGES_PER_YEAR',
  UNAVAILABLE = 'COLLEGE_EXCHANGE_FUND_NOTE_UNAVAILABLE',
  UNAVAILABLE_FOR_REDEMPTION_ONLINE = 'COLLEGE_EXCHANGE_FUND_NOTE_UNAVAILABLE_FOR_REDEMPTION_ONLINE',
  EXCHANGE_LIMIT_REACHED = 'COLLEGE_EXCHANGE_FUND_NOTE_EXCHANGE_LIMIT_REACHED',
}

export enum FundNoteAutoRMD {
  AUTO_RMD = 'AUTO_RMD',
}

export type FundNote =
  | FundNoteAdd
  | FundNoteBuy
  | FundNoteAutoBuy
  | FundNoteAutoExchange
  | FundNoteSell
  | FundNoteDistributeCollege
  | FundNoteExchange
  | FundNoteCollege
  | FundNoteExchangeCollege
  | FundNoteRothConversion
  | FundNoteAutoRMD;

export enum DELIVERY_METHOD {
  MAIL_A_CHECK = 'MAIL_A_CHECK',
  MAIL_A_CHECK_QCD = 'MAIL_A_CHECK_QCD',
  WIRE = 'WIRE',
  ACH = 'ACH',
  IN_KIND = 'IN_KIND',
  MAIL_A_CHECK_SCHOOL = 'MAIL_A_CHECK_SCHOOL',
  MAIL_A_CHECK_CHARITY = 'MAIL_A_CHECK_CHARITY',
}
export const PRODUCT_TYPE_SORT_ENUM_MAP = {
  NATIONAL_529: 50,
  MARYLAND_529: 60,
  ALASKA_529: 70,

  National529: 50,
  Maryland529: 60,
  Alaska529: 70,
};

export const DISPLAY_TYPE_SORT_ENUM_MAP = {
  RETIREMENT: 1,
  NON_RETIREMENT: 2,
  COLLEGE_SAVINGS: 3,
};

/** @type {Object.<string, number>} */
export const PRODUCT_TYPE_ENUM_MAP: Record<string, number> = {
  MUTUAL_FUND: 10,
  BROKERAGE: 20,
  VARIABLE_ANNUITY: 30,
  WORKPLACE_RETIREMENT: 50,
  RETIREMENT_PLAN: 60,
  DEFINED_BENEFIT: 61,
  RPS_TRADELINK_SUMMARY: 62,

  RPS_TRADELINK_INDIVIDUAL: 63,
  NATIONAL_529: 80,
  MARYLAND_529: 40,
  ALASKA_529: 70,
  EXTERNAL_PLAN: 90,
  JOHN_HANCOCK_529: 100,
  MANAGED_ACCOUNT: 110,
  RETIREMENT_ADVISORY: 120,

  // mapping both the Java keys and the Java names since both are used in different places
  MutualFund: 10,
  Brokerage: 20,
  VariableAnnuity: 30,
  WorkplaceRetirement: 50,
  RetirementPlan: 60,
  DefinedBenefit: 61,
  RPSTradeLinkSummary: 62,

  RPSTradeLinkIndividual: 63,
  National529: 80,
  Maryland529: 40,
  Alaska529: 70,
  'External plans': 90,
  'John Hancock 529': 100,
  ManagedAccount: 110,
  RetirementAdvisory: 120,
};

export const PRODUCT_TYPE_LABEL_MAP = {
  MUTUAL_FUND: 'Mutual Fund',
  BROKERAGE: 'Brokerage',
  VARIABLE_ANNUITY: 'Variable Annuity',
  MARYLAND_529: 'Maryland Senator Edward J. Kasemeyer College Investment Plan',
  WORKPLACE_RETIREMENT: 'Workplace Retirement',
  RETIREMENT_PLAN: 'Small Business Retirement',
  DEFINED_BENEFIT: 'Defined Benefit',
  RPS_TRADELINK_SUMMARY: 'Workplace Retirement',

  RPS_TRADELINK_INDIVIDUAL: 'Workplace Retirement',
  ALASKA_529: 'Alaska 529',
  NATIONAL_529: `T.${NBSP}Rowe${NBSP}Price College Savings Plan`,
  EXTERNAL_PLAN: 'External Plan',
  JOHN_HANCOCK_529: 'John Hancock 529 College Savings Plan',
  MANAGED_ACCOUNT: 'ActivePlus Portfolios®',

  // mapping both the Java keys and the Java names since both are used in different places
  MutualFund: 'Mutual Fund',
  Brokerage: 'Brokerage',
  VariableAnnuity: 'Variable Annuity',
  Maryland529: 'Maryland Senator Edward J. Kasemeyer College Investment Plan',
  WorkplaceRetirement: 'Workplace Retirement',
  RetirementPlan: 'Small Business Retirement',
  DefinedBenefit: 'Defined Benefit',
  RPSTradeLinkSummary: 'Workplace Retirement',

  RPSTradeLinkIndividual: 'Workplace Retirement',
  Alaska529: 'Alaska 529',
  National529: `T.${NBSP}Rowe${NBSP}Price College Savings Plan`,
  'External plans': 'External Plan',
  'John Hancock 529': 'John Hancock 529 College Savings Plan',
  ManagedAccount: 'ActivePlus Portfolios®',
};

export const BRANDING_CONTACT_NUMBERS = {
  ALASKA_529: '1-800-478-0003',
  MARYLAND_529: '(888)-463-4723',
  NATIONAL_529: '1-866-521-1894',
};

export const TRANSACTION_TYPE_ENUM_MAP = {
  [TransactionTypes.BUY]: 0,
  [TransactionTypes.SELL]: 131,
};

export const PRODUCT_TYPE_ENUMERATION_NAME = {
  MANAGED_ACCOUNT: 'ManagedAccount',
  ADVICE_MANAGED: 'AdviceManaged',
};
export const IAS_DELIVERY_DIRECT_DEPOSIT = 'ElectronicTransferToBank';
export const IAS_DELIVERY_MAIL_CHECK = 'CheckToAddress';
export const IAS_DELIVERY_EXPRESS_DELIVERY = 'WireToBank';
export const IAS_DELIVERY_IN_KIND = 'InKindTransfer';

export const DELIVERY_DIRECT_DEPOSIT = 'OneTimeAch';
export const DELIVERY_MAIL_CHECK = 'Check';
export const DELIVERY_MAIL_CHECK_QCD = 'CheckQCD';
export const DELIVERY_MAIL_CHECK_TO_SCHOOL = 'CheckToSchool';
export const DELIVERY_MAIL_CHECK_TO_CHARITY = 'CheckToCharity';
export const DELIVERY_EXPRESS_DELIVERY = 'Wire';
export const DELIVERY_IN_KIND = 'InKindTransfer';

export const AUTO_DELIVERY_ENUM = {
  [DELIVERY_DIRECT_DEPOSIT]: {
    enumerationID: 6,
    enumerationName: IAS_DELIVERY_DIRECT_DEPOSIT,
  },
  [DELIVERY_MAIL_CHECK]: {
    enumerationID: 7,
    enumerationName: IAS_DELIVERY_MAIL_CHECK,
  },
};

export const DELIVERY_METHODS: Record<DELIVERY_METHOD, string> = {
  [DELIVERY_METHOD.ACH]: DELIVERY_DIRECT_DEPOSIT,
  [DELIVERY_METHOD.MAIL_A_CHECK]: DELIVERY_MAIL_CHECK,
  [DELIVERY_METHOD.MAIL_A_CHECK_QCD]: DELIVERY_MAIL_CHECK_QCD,
  [DELIVERY_METHOD.WIRE]: DELIVERY_EXPRESS_DELIVERY,
  [DELIVERY_METHOD.IN_KIND]: DELIVERY_IN_KIND,
  [DELIVERY_METHOD.MAIL_A_CHECK_SCHOOL]: DELIVERY_MAIL_CHECK_TO_SCHOOL,
  [DELIVERY_METHOD.MAIL_A_CHECK_CHARITY]: DELIVERY_MAIL_CHECK_TO_CHARITY,
};

export const DELIVERY_ENUM_MAP = {
  [DELIVERY_METHODS.ACH]: 6,
  [DELIVERY_METHODS.MAIL_A_CHECK]: 7,
  [DELIVERY_METHODS.WIRE]: 5,
  [DELIVERY_METHODS.IN_KIND]: 8,
};

export const DELIVERY_PRINT = {
  [DELIVERY_METHODS.ACH]: 'DIRECT DEPOSIT',
  [DELIVERY_METHODS.MAIL_A_CHECK]: 'MAIL CHECK TO',
  [DELIVERY_METHODS.WIRE]: 'WIRE TRANSFER',
  [DELIVERY_METHODS.IN_KIND]: 'In Kind Transfer',
};

export const REDEMPTION_SUBMISSION_DELIVERY_TYPE_ENUM = {
  [DELIVERY_METHODS.ACH]: IAS_DELIVERY_DIRECT_DEPOSIT,
  [DELIVERY_METHODS.MAIL_A_CHECK]: IAS_DELIVERY_MAIL_CHECK,
  [DELIVERY_METHODS.WIRE]: IAS_DELIVERY_EXPRESS_DELIVERY,
  [DELIVERY_METHODS.IN_KIND]: IAS_DELIVERY_IN_KIND,
};

export const DELIVERY_METHOD_REQUIRED_ENUM = {
  [DELIVERY_DIRECT_DEPOSIT]: SystemCode.ACH_REDEMPTION,
  [DELIVERY_EXPRESS_DELIVERY]: SystemCode.WIRE_REDEMPTION,
};

export const MAXIMUM_BY_DELIVERY_METHOD = {
  [DELIVERY_DIRECT_DEPOSIT]: MAXIMUM_ACH_REDEMPTION,
  [DELIVERY_EXPRESS_DELIVERY]: MAXIMUM_WIRE_REDEMPTION,
  [DELIVERY_MAIL_CHECK]: MAXIMUM_CHECK_REDEMPTION,
};

// These are auto-enumerated as numbers when compiled to JS.
// DO NOT CHANGE THE ORDER
export enum SelectedUnitNum {
  DOLLAR = 1,
  SHARES,
  PERCENT,
  ALLSHARES,
  ALLREMAINING,
}

export enum CodeToSelectedUnit {
  'D' = UNIT.DOLLARS,
  'S' = UNIT.SHARES,
  'P' = UNIT.PERCENT,
  'AS' = UNIT.ALL_SHARES,
  'AR' = UNIT.ALL_REMAINING,
}

// Order doesn't matter here
export enum SelectedUnitAlpha {
  D = 'D',
  S = 'S',
  P = 'P',
  AS = 'AS',
  AR = 'AR',
}

export enum SelectedUnitLower {
  DOLLARS = 'Dollars',
  PERCENT = 'Percent',
  SHARES = 'Shares',
  ALLSHARES = 'AllShares',
  ALLREMAINING = 'AllRemaining',
}
export interface DisbursementType {
  enumerationID: SelectedUnitNum;
  enumerationName: SelectedUnitLower;
  code?: SelectedUnitAlpha;
}

export const SELECTED_UNIT_ALPHA_MAP: Record<UNIT, SelectedUnitAlpha> = {
  [UNIT.DOLLARS]: SelectedUnitAlpha.D,
  [UNIT.PERCENT]: SelectedUnitAlpha.P,
  [UNIT.SHARES]: SelectedUnitAlpha.S,
  [UNIT.ALL_SHARES]: SelectedUnitAlpha.AS,
  [UNIT.ALL_REMAINING]: SelectedUnitAlpha.AR,
};

export const SELECTED_UNIT_CASE_SENSITIVE_MAP: Record<UNIT, SelectedUnitLower> =
  {
    [UNIT.DOLLARS]: SelectedUnitLower.DOLLARS,
    [UNIT.PERCENT]: SelectedUnitLower.PERCENT,
    [UNIT.SHARES]: SelectedUnitLower.SHARES,
    [UNIT.ALL_SHARES]: SelectedUnitLower.ALLSHARES,
    [UNIT.ALL_REMAINING]: SelectedUnitLower.ALLREMAINING,
  };

// https://github.awstrp.net/personal-investor/transaction-exchange-service/blob/master/service/src/main/java/com/trp/transaction/exchange/model/constants/TransactionConstants.java
export const SELECTED_UNIT_ENUM_OBJECT_MAP: Record<UNIT, DisbursementType> = {
  [UNIT.DOLLARS]: {
    enumerationID: SelectedUnitNum.DOLLAR,
    enumerationName: SelectedUnitLower.DOLLARS,
    code: SelectedUnitAlpha.D,
  },
  [UNIT.PERCENT]: {
    enumerationID: SelectedUnitNum.PERCENT,
    enumerationName: SelectedUnitLower.PERCENT,
    code: SelectedUnitAlpha.P,
  },
  [UNIT.SHARES]: {
    enumerationID: SelectedUnitNum.SHARES,
    enumerationName: SelectedUnitLower.SHARES,
  },
  [UNIT.ALL_SHARES]: {
    enumerationID: SelectedUnitNum.ALLSHARES,
    enumerationName: SelectedUnitLower.ALLSHARES,
  },
  [UNIT.ALL_REMAINING]: {
    enumerationID: SelectedUnitNum.ALLREMAINING,
    enumerationName: SelectedUnitLower.ALLREMAINING,
  },
};

export const MONEY_MARKET_EVENT_FEE = 'Fee';
export const MONEY_MARKET_EVENT_GATE = 'Gate';
export const MONEY_MARKET_EVENT_PERIOD_IN_EVENT = 'In Event';

export const PERSONAL_SERVICES_HIGH_NET_WORTH_NUMBER = '(800) 225-3222';
export const TOLL_FREE_NUMBER = '(800) 225-5132';
export const COLLEGE_SAVINGS_TOLL_FREE_NUMBER = '(866) 701-2193';

export const APP_TOLL_FREE_NUMBER = '(855) 787-9140';

export enum AddFundType {
  BENEFICIARY = 'beneficiary',
  ACCOUNT = 'account',
}
export interface AddFundModalProp {
  addType: AddFundType;
  fundType: 'portfolio' | 'fund';
  groupTypeFragment: string;
  showProspectus: boolean;
  showSymbol: boolean;
  showIClass: boolean;
  url?: URL['pathname'];
}

export const ADD_FUND_MODAL_PROPS: Record<string, AddFundModalProp> = {
  MD_COLLEGE: {
    addType: AddFundType.BENEFICIARY,
    fundType: 'portfolio',
    groupTypeFragment: '',
    showProspectus: true,
    showSymbol: false,
    showIClass: false,
    url: 'https://www.troweprice.com/usis/nasu/start/college-savings/maryland',
  },
  AK_COLLEGE: {
    addType: AddFundType.BENEFICIARY,
    fundType: 'portfolio',
    groupTypeFragment: '',
    showProspectus: true,
    showSymbol: false,
    showIClass: false,
    url: 'https://www.troweprice.com/usis/nasu/start/college-savings/alaska',
  },
  NAT_COLLEGE: {
    addType: AddFundType.BENEFICIARY,
    fundType: 'portfolio',
    groupTypeFragment: '',
    showProspectus: true,
    showSymbol: false,
    showIClass: false,
    url: 'https://www.troweprice.com/usis/nasu/start/college-savings/national',
  },
  MIXED_COLLEGE: {
    addType: AddFundType.BENEFICIARY,
    fundType: 'portfolio',
    groupTypeFragment: '',
    showProspectus: true,
    showSymbol: false,
    showIClass: false,
    url: 'https://www.troweprice.com/usis/nasu/account-finder',
  },
  DEFAULT: {
    addType: AddFundType.ACCOUNT,
    fundType: 'fund',
    groupTypeFragment: 'to your portfolio.',
    showProspectus: true,
    showSymbol: true,
    showIClass: true,
  },
};

export enum COLLEGE_PLAN_TYPES {
  MARYLAND_529 = 'Maryland529',
  NATIONAL_529 = 'National529',
  ALASKA_529 = 'Alaska529',
}

export enum COLLEGE_DISTRIBUTE_PAYEE_OPTIONS {
  ACCOUNT_HOLDER = 'Account Holder',
  BENEFICIARY = 'Beneficiary',
}

export interface CollegeDistributePayee {
  id: number;
  name: string;
}

export const COLLEGE_DISTRIBUTE_PAYEE_OPTIONS_ENUM_MAP: Record<
  string,
  CollegeDistributePayee
> = {
  ACCOUNT_HOLDER: {
    id: 10,
    name: 'AccountHolder',
  },
  BENEFICIARY: {
    id: 20,
    name: 'Beneficiary',
  },
};

export type CollegePlanRecord = Record<COLLEGE_PLAN_TYPES, string | string[]>;

export const collegeMaxPlanLimits: Record<COLLEGE_PLAN_TYPES, { max: number }> =
  {
    [COLLEGE_PLAN_TYPES.MARYLAND_529]: {
      max: 500000,
    },
    [COLLEGE_PLAN_TYPES.NATIONAL_529]: {
      max: 550000,
    },
    [COLLEGE_PLAN_TYPES.ALASKA_529]: {
      max: 550000,
    },
  };

export const collegeGiftingPortals: CollegePlanRecord = {
  [COLLEGE_PLAN_TYPES.MARYLAND_529]: 'maryland-529',
  [COLLEGE_PLAN_TYPES.NATIONAL_529]: 'national-529',
  [COLLEGE_PLAN_TYPES.ALASKA_529]: 'alaska-529',
};

const costBasisExclusionTypes = {
  EDUCATION_SAVINGS_ACCOUNT: false,
  MONEY_MARKET: false,
};

export const isCostBasisApplicable = (agreementType: string): boolean =>
  !(agreementType.endsWith('IRA') || costBasisExclusionTypes[agreementType]);

export const COLLEGE_FUND_NOTE_PHONE_NUMBERS: CollegePlanRecord = {
  [COLLEGE_PLAN_TYPES.MARYLAND_529]: ['888.4MD.GRAD', '463.4723'],
  [COLLEGE_PLAN_TYPES.NATIONAL_529]: '(866) 521-1894',
  [COLLEGE_PLAN_TYPES.ALASKA_529]: '(800) 478-0003',
};

export const PHONE_NUMBERS_BY_PRODUCT_TYPE = {
  ...COLLEGE_FUND_NOTE_PHONE_NUMBERS,
  MutualFund: TOLL_FREE_NUMBER, // Not using constant since the product type constant doesn't actually map with product types sent from server
  undefined: TOLL_FREE_NUMBER,
};

export const APP_SUPPORT_PHONE_NUMBER = '(855)\xa0787-9140';
export const RAS_SUPPORT_PHONE_NUMBER = '(877)\xa0455-2225';

export const RAS_GENERAL_TIMETRADE_SCHEDULING_URL =
  'https://trpii-events.secure.force.com/cts?Id=4KIAWGyTfVGt0.v.GIJG1vZ2zOfAR8WD91EB8jMK6M8-&userIds=Financial_Advisor_Team';

export const collegeDistributeReasons: { label: string; value: number }[] = [
  { label: 'College Expenses', value: 10 },
  { label: 'K-12 Expenses', value: 20 },
  { label: 'Apprenticeship Program Expenses', value: 50 },
  { label: 'Education Loan Payments', value: 60 },
  { label: 'Other', value: 30 },
  { label: 'Prefer Not To Provide', value: 40 },
];

export const MIN_REDEEMABLE_THRESHOLD_DOLLAR_PERCENTAGE = 85;

export const EXCHANGE_DIVIDEND_PHONENUMBER = '(877) 218-5162';
export const COLLEGE_DIVIDEND_PHONENUMBER = '(866) 521-1894';

export const collegeDisclaimerMap = {
  alaska: {
    name: PRODUCT_TYPE_LABEL_MAP.Alaska529 + ' Plan Disclosure Document',
    type: 'ak529',
    url: 'https://www.alaska529plan.com/content/dam/alaska529/forms/Plan_Disclosure.pdf',
  },
  maryland: {
    name: PRODUCT_TYPE_LABEL_MAP.Maryland529 + ' Disclosure Statement',
    type: 'md529',
    url: 'https://maryland529.com/Portals/0/Files/MCIP_Disclosure_Statement.pdf',
  },
  national: {
    name: PRODUCT_TYPE_LABEL_MAP.National529 + ' Disclosure Document',
    type: 'nat529',
    url: 'https://individual.troweprice.com/Retail/Shared/PDFs/trp529Disclosure.pdf',
  },
};
export const COLLEGE_FUND_NOTE_CANNOT_LINK_BANK_FORM_URL: CollegePlanRecord = {
  [COLLEGE_PLAN_TYPES.MARYLAND_529]:
    'https://maryland529.com/Portals/0/Forms/MCIPAccountServices.pdf?ver=2018-11-12-143448-353',
  [COLLEGE_PLAN_TYPES.NATIONAL_529]:
    'https://individual.troweprice.com/staticFiles/Retail/Shared/PDFs/trpUpdateYourAccServicesForm.pdf',
  [COLLEGE_PLAN_TYPES.ALASKA_529]:
    'https://www.alaska529plan.com/content/dam/alaska529/forms/uaUpdateYourAccServicesForm.pdf',
};

export const EXCHANGE_DEFAULT_MIN = 0.01;
export const EXCHANGE_DEFAULT_MAX = 500000;
export const ROTH_CONVERSION_DEFAULT_MIN = 0.01;
export const ROTH_CONVERSION_NEW_DEFAULT_MIN = 1000;
export const ROTH_CONVERSION_DEFAULT_MAX = 500000;
export const ROTH_CONVERSION_TAX_MAX_P = 99;
export const ROTH_CONVERSION_TAX_MIN_P = 1;

export enum ROTH_CONVERSION_ALLOCATION_TYPES {
  SAME_FUNDS = 'SAME_FUNDS',
}

export enum ROTH_CONVERSION_TAX_OPTIONS {
  YES_WITHHOLD = 'YES_WITHHOLD',
  NO_DONT_WITHHOLD = 'NO_DONT_WITHHOLD',
}

export enum ROTH_CONVERSION_BENEFICIARY_OPTIONS {
  EXISTING_ROTH_IRA = 'EXISTING_ROTH_IRA',
  EXISTING_TRADITIONAL_IRA = 'EXISTING_TRADITIONAL_IRA',
  EXISTING_ROLLOVER_IRA = 'EXISTING_ROLLOVER_IRA',
  CREATE_NEW = 'CREATE_NEW',
  SETUP_LATER = 'SETUP_LATER',
}

export const CONFIRM_PATH_SUFFIX = '/confirm';

export enum ROTH_CONVERSION_BENEFICIARY_OPTIONS_LABEL {
  EXISTING_ROTH_IRA = 'Use existing Roth IRA beneficiaries ',
  EXISTING_TRADITIONAL_IRA = 'Use existing Traditional IRA beneficiaries ',
  EXISTING_ROLLOVER_IRA = 'Use Existing Rollover IRA beneficiaries',
  CREATE_NEW = 'Created new beneficiaries',
  SETUP_LATER = 'Set up later',
}

export enum BENEFICIARY_TYPES {
  INDIVIDUAL = 'individual',
  ENTITY = 'entity',
}

export enum BENEFICIARY_CATEGORY {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
}

// configs used for referencing when creating payload for roth conversion submission
export const BENEFICIARY_CATEGORY_CONFIG = {
  primary: {
    enumerationID: 0,
    enumerationName: 'p',
  },
  secondary: {
    enumerationID: 1,
    enumerationName: 's',
  },
};

export const BENEFICIARY_RELATIONSHIP_CONFIG = {
  parent: {
    enumerationID: 100,
    enumerationName: 'p',
    displayName: 'Parent',
  },
  spouse: {
    enumerationID: 101,
    enumerationName: 's',
    displayName: 'Spouse',
  },
  child: {
    enumerationID: 102,
    enumerationName: 'c',
    displayName: 'Child',
  },
  sibling: {
    enumerationID: 103,
    enumerationName: 'b',
    displayName: 'Sibling',
  },
  other: {
    enumerationID: 104,
    enumerationName: 'o',
    displayName: 'Other',
  },
};

export const BENEFICIARY_ENTITYTYPE_CONFIG = {
  individual: {
    individualOrOrganizationCode: 'I',
    enumerationID: 1,
    enumerationName: 'Customer',
  },
  entity: {
    individualOrOrganizationCode: 'O',
    enumerationID: 2,
    enumerationName: 'BusinessEntity',
  },
};

export const NEW_ICLASS_FUND_MIN_AMOUNT = 50000;
export const EXISTING_ICLASS_FUND_MIN_AMOUNT = 100;

export const IRA_AUTOMAX_REMAINING_ERROR = 100;

export const ZERO_FUND_NON_RETIREMENT = 2500;
export const ZERO_FUND_RETIREMENT = 1000;

export const RMD_ELIGIBLE_AGE = 73;

export const OK_AND_DC_TAX_EVENT = 'November 18, 2022, 16:00:00';

export const PERCENT_START_DATE_TOOLTIP =
  'Because you entered the redemption amount as a percentage or number of shares, an estimated calculation is shown here. Annual Percentage elections will display the full annual distribution. Exact values will be calculated at the time the trade is placed.';

export const YEAR_2023 = 2023;
export const YEAR_1950 = 1950;
export const YEAR_1949 = 1949;
export const YEAR_2022 = 2022;
export const YEAR_2021 = 2021;

export const withHoldStateRegardless = ['DC', 'MI', 'CT', 'MN', 'OK'];

export const pershingVoluntaryStateWithholdings = [
  'AL',
  'AZ',
  'CO',
  'DE',
  'GA',
  'ID',
  'IL',
  'IN',
  'KY',
  'LA',
  'MO',
  'MT',
  'ND',
  'NJ',
  'NM',
  'NY',
  'OH',
  'PA',
  'RI',
  'SC',
  'UT',
  'VA',
  'WI',
  'WV',
  'MD',
];
